<template>
  <CDropdown
    v-if="pitcherInfo?.apiOrigin && pitcherInfo?.accessToken && launchDarkly.allow_ai_prompts_in_canvas_text"
    :on-clickoutside="handleClickOutside"
    :options="isLoading || aiActionResponse ? responseStreamingOverlay : aiActionOptions"
    placement="bottom-end"
    :show="showDropdown"
    :themeOverrides="themeOverrides"
    @select="(s: string) => aiActionHandler(s)"
  >
    <div>
      <CButton :themeOverrides="themeOverrides.default" @click="onClick">
        <template #icon>
          <CIcon faType="fas" icon="cat-space" :size="16" />
        </template>
        {{ t('canvasUI.CAiHtmlActions.aiAssistant') }}
      </CButton>
    </div>
  </CDropdown>
</template>

<script lang="tsx" setup>
import { ref, inject, computed, type ComputedRef, type StyleValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { type DropdownOption, NButton, NInput } from 'naive-ui'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { PitcherInfo } from '@lib/types/app'
import useToast from '@lib/composables/useToast'
import { requestStream } from '@lib/util/network'
import useCanvas from '@canvas-builder/composables/useCanvas'

interface Props {
  modelValue: string
  themeOverrides?: { active: StyleValue; default: StyleValue }
}

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => undefined),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { toast } = useToast()
const { activeCanvas } = useCanvas()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  startLoading: []
  endLoading: []
}>()

const props = withDefaults(defineProps<Props>(), {
  themeOverrides: () => ({ active: {}, default: {} }),
})

const { t } = useI18n()
const showDropdown = ref(false)
const isLoading = ref(false)
const aiActionResponse = ref('')
const abortController = ref<AbortController | null>(null)

const htmlIsEmpty = computed(() => !props.modelValue || props.modelValue.trim() === '')

const currentAction = ref<string>('')

const aiActionOptions = computed(() => {
  // If we're showing an input for custom or generateComponent, show that instead
  if (currentAction.value === 'custom') {
    return [createInputOption('custom', t('canvasUI.CAiHtmlActions.enterCustomPrompt'))]
  }
  if (currentAction.value === 'generateComponent') {
    return [createInputOption('generateComponent', t('canvasUI.CAiHtmlActions.describeComponent'))]
  }

  // Otherwise show the main menu
  return [
    {
      disabled: htmlIsEmpty.value,
      icon: () => <CIcon icon="code" />,
      key: 'improveCode',
      label: t('canvasUI.CAiHtmlActions.improveCode'),
    },
    {
      disabled: htmlIsEmpty.value,
      icon: () => <CIcon icon="magic" />,
      key: 'makeResponsive',
      label: t('canvasUI.CAiHtmlActions.makeResponsive'),
    },
    {
      disabled: htmlIsEmpty.value,
      icon: () => <CIcon icon="bug" />,
      key: 'fixErrors',
      label: t('canvasUI.CAiHtmlActions.fixErrors'),
    },
    {
      disabled: htmlIsEmpty.value,
      icon: () => <CIcon icon="compress" />,
      key: 'minify',
      label: t('canvasUI.CAiHtmlActions.minify'),
    },
    {
      disabled: htmlIsEmpty.value,
      icon: () => <CIcon icon="paint-brush" />,
      key: 'addStyling',
      label: t('canvasUI.CAiHtmlActions.addStyling'),
    },
    {
      type: 'divider',
      key: 'divider1',
    },
    {
      icon: () => <CIcon icon="file-code" />,
      key: 'generateComponent',
      label: t('canvasUI.CAiHtmlActions.generateComponent'),
    },
    {
      type: 'divider',
      key: 'divider2',
    },
    {
      icon: () => <CIcon icon="terminal" />,
      key: 'custom',
      label: t('canvasUI.CAiHtmlActions.customPrompt'),
    },
  ]
})

const lastUsedPrompts = ref<Record<string, string>>({})

const createInputOption = (key: string, placeholder: string) => ({
  render: () => (
    <div class={`${key}-input-container custom-user-input-container p-2 w-[400px]`}>
      <NInput
        type="textarea"
        placeholder={placeholder}
        defaultValue={lastUsedPrompts.value[key] || ''}
        onKeydown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleInputSubmit(key, e.target as HTMLTextAreaElement)
          }
          e.stopPropagation()
        }}
      />
      <div style={{ textAlign: 'left', marginTop: '10px' }}>
        <NButton
          type="primary"
          onClick={(e) => {
            const textarea = (e.target as HTMLElement).closest(`.${key}-input-container`)?.querySelector('textarea')
            if (textarea) {
              handleInputSubmit(key, textarea)
            }
          }}
        >
          {t('canvasUI.CAiHtmlActions.submitButton')}
        </NButton>
      </div>
    </div>
  ),
  type: 'render',
  key: `${key}-input`,
})

const responseStreamingOverlay: DropdownOption[] = [
  {
    render: () => (
      <div class="w-[600px] min-h-[300px] max-h-[500px] p-3 flex flex-col">
        <div class="mb-2 font-semibold">{t('canvasUI.CAiHtmlActions.aiSuggestion')}</div>
        <div class="flex-1 overflow-auto border border-solid border-primary5 rounded-2 p-3 bg-gray-50">
          <pre class="font-mono text-sm whitespace-pre-wrap">{aiActionResponse.value || 'Loading...'}</pre>
        </div>
        <div class="mt-3 flex justify-between items-center">
          <div class="text-sm text-gray-600">
            {isLoading.value ? t('canvasUI.CAiHtmlActions.generatingCode') : t('canvasUI.CAiHtmlActions.reviewChanges')}
          </div>
          <div class="flex gap-2">
            <NButton type="primary" disabled={isLoading.value} onClick={acceptResponse}>
              {t('canvasUI.CAiHtmlActions.applyChanges')}
            </NButton>
            <NButton onClick={isLoading.value ? cancelStreaming : hideAndReset}>
              {isLoading.value ? t('canvasUI.CAiHtmlActions.cancel') : t('canvasUI.CAiHtmlActions.reject')}
            </NButton>
          </div>
        </div>
      </div>
    ),
    type: 'render',
    key: 'response-streaming',
  },
]

function handleInputSubmit(key: string, inputElement: HTMLTextAreaElement) {
  const value = inputElement.value
  if (!value.trim()) return

  lastUsedPrompts.value[key] = value
  currentAction.value = '' // Reset to show main menu after submission

  if (key === 'custom') {
    aiActionHandler('custom-input', { customPrompt: value })
  } else if (key === 'generateComponent') {
    aiActionHandler('generateComponent-input', { description: value })
  }
}

function onClick() {
  showDropdown.value = !showDropdown.value
}

function reset() {
  aiActionResponse.value = ''
  currentAction.value = ''
  abortController.value?.abort()
  abortController.value = null
}

function hideAndReset() {
  showDropdown.value = false
  reset()
  isLoading.value = false
  emit('endLoading')
}

function handleClickOutside() {
  if (!isLoading.value && !aiActionResponse.value) {
    showDropdown.value = false
    reset()
  }
}

const SIMPLE_PROMPT_ACTIONS = {
  improveCode:
    'Improve the following HTML code by fixing issues, improving structure, and following best practices. Keep the same functionality. Use unique class names with "cb-html-" prefix for any styles. Never use global CSS selectors. Preserve any Handlebars template expressions ({{...}}, {{#if}}, {{#each}}, etc.):',
  makeResponsive:
    'Make the following HTML responsive with proper mobile-first design. Use unique class names with "cb-html-" prefix for all styles. Never use global CSS selectors like body, div, p, etc. Preserve any Handlebars template expressions:',
  fixErrors:
    'Fix any HTML syntax errors, close unclosed tags, and ensure valid HTML5. If adding any styles, use unique class names with "cb-html-" prefix. Preserve any Handlebars template expressions:',
  minify:
    'Minify the following HTML by removing unnecessary whitespace while keeping it readable. Preserve all class names and styles. Preserve any Handlebars template expressions:',
  addStyling:
    'Add CSS styling to make the following HTML more visually appealing. IMPORTANT: Use unique class names with "cb-html-" prefix. Never use global selectors. Use either inline styles or <style> with scoped class selectors only. Preserve any Handlebars template expressions:',
} as const

async function aiActionHandler(action: string, extra?: Record<string, any>) {
  // Handle input display for custom and generateComponent
  if (action === 'custom' || action === 'generateComponent') {
    currentAction.value = action
    return // The computed property will handle showing the input
  }

  const currentHtml = props.modelValue || ''
  const htmlContent = currentHtml ? `---START-HTML---\n${currentHtml}\n---END-HTML---` : ''

  // Get available context variables if canvas has context
  const contextInfo = activeCanvas.value?.context
    ? `\n\nAvailable Handlebars context variables:\n${JSON.stringify(Object.keys(activeCanvas.value.context), null, 2)}\n\nYou can use these variables in Handlebars expressions like {{variableName}} or in conditionals like {{#if variableName}}...{{/if}}.`
    : ''

  const promptPrefix = `You are an HTML code assistant. Only return valid HTML code without any markdown formatting or explanations. The response should be pure HTML that can be directly used.

IMPORTANT STYLING RULES:
1. NEVER use global CSS selectors like body, html, *, h1, h2, div, p, etc. as they will affect the entire page
2. ALWAYS use unique class names with a specific prefix like "cb-html-" (e.g., cb-html-container, cb-html-title, cb-html-content)
3. When using <style> tags, scope ALL CSS rules to unique class names
4. Use inline styles or scoped classes only - never global element selectors
5. Prefix all class names to avoid conflicts with existing page styles

IMPORTANT: This HTML component supports Handlebars templating. The HTML code will be processed with Handlebars before rendering, so you can use Handlebars syntax like {{variable}}, {{#if condition}}, {{#each array}}, etc. When the user's HTML contains Handlebars expressions, preserve them. You can also suggest using Handlebars templating for dynamic content when appropriate.${contextInfo} `

  aiActionResponse.value = ''

  if (Object.hasOwn(SIMPLE_PROMPT_ACTIONS, action)) {
    await handleSimplePrompt(action as keyof typeof SIMPLE_PROMPT_ACTIONS, htmlContent, promptPrefix)
  } else if (action === 'custom-input' && extra?.customPrompt) {
    await handleCustomPrompt(extra.customPrompt, htmlContent, promptPrefix)
  } else if (action === 'generateComponent-input' && extra?.description) {
    await handleGenerateComponent(extra.description, promptPrefix)
  }
}

async function handleSimplePrompt(
  action: keyof typeof SIMPLE_PROMPT_ACTIONS,
  htmlContent: string,
  promptPrefix: string,
) {
  reset()
  await prompt(promptPrefix + `${SIMPLE_PROMPT_ACTIONS[action]} ${htmlContent}`)
}

async function handleCustomPrompt(customPrompt: string, htmlContent: string, promptPrefix: string) {
  reset()
  const styleReminder =
    '\n\nREMEMBER: Use unique class names with "cb-html-" prefix for all CSS. Never use global selectors that could affect the entire page.'
  const fullPrompt = htmlContent
    ? `${promptPrefix}\n\nAction: ${customPrompt}${styleReminder}\n\nPreserve any existing Handlebars expressions and feel free to suggest Handlebars templating for dynamic content where appropriate.\n\nHTML Code: ${htmlContent}`
    : `${promptPrefix}\n\nGenerate HTML for: ${customPrompt}${styleReminder}\n\nYou can use Handlebars templating ({{variable}}, {{#if}}, {{#each}}, etc.) for dynamic content where appropriate.`
  await prompt(fullPrompt)
}

async function handleGenerateComponent(description: string, promptPrefix: string) {
  reset()
  await prompt(
    `${promptPrefix}\n\nGenerate a complete HTML component for: ${description}. Include appropriate styling and make it responsive.

CRITICAL: All CSS must use unique class names with "cb-html-" prefix (e.g., cb-html-wrapper, cb-html-header). Never use global selectors like body, html, div, p, h1, etc. All styles must be scoped to prevent affecting other page elements.

You can use Handlebars templating ({{variable}}, {{#if}}, {{#each}}, etc.) for dynamic content where it makes sense.`,
  )
}

function acceptResponse() {
  const response = aiActionResponse.value
  if (response) {
    emit('update:modelValue', response)
  }
  hideAndReset()
}

function cancelStreaming() {
  abortController.value?.abort()
  toast.info(t('canvasUI.CAiHtmlActions.cancelled'))
  hideAndReset()
}

async function prompt(text: string) {
  if (!pitcherInfo.value?.apiOrigin || !pitcherInfo.value?.accessToken) {
    return Promise.reject(new Error('Missing required pitcher info'))
  }

  emit('startLoading')
  isLoading.value = true
  abortController.value = new AbortController()

  try {
    const codeModel = launchDarkly.value.ai_code_generation_model || 'anthropic/claude-4-sonnet'

    const result = await requestStream(
      `${import.meta.env.DEV ? '' : pitcherInfo.value.apiOrigin}/core/api/protected/ai/ai-generation/stream`,
      {
        version: codeModel,
        stream: true,
        input: {
          prompt: text,
          temperature: 0.3,
          max_tokens: 2048,
        },
      },
      pitcherInfo.value.accessToken,
      (chunk, response) => {
        try {
          const lines = chunk.split('\n').filter(Boolean)
          let fullOutput = ''

          for (const line of lines) {
            const data = JSON.parse(line)

            if (data.status === 'succeeded' && data.output) {
              if (Array.isArray(data.output)) {
                fullOutput = data.output.join('')
              } else {
                fullOutput = data.output
              }
              break
            }

            if (data.output) {
              fullOutput += data.output
            }
          }

          if (fullOutput) {
            aiActionResponse.value = fullOutput
          }
        } catch (err) {
          aiActionResponse.value = response
        }
      },
      abortController.value.signal,
    )
    return result
  } catch (e: unknown) {
    if ((e as any)?.name === 'AbortError') {
      console.log('Streaming was cancelled')
      return
    }
    console.error('AI prompt failed:', e)
    toast.error(t('canvasUI.CAiHtmlActions.requestFailed'))
    throw e
  } finally {
    emit('endLoading')
    isLoading.value = false
    abortController.value = null
  }
}
</script>
